export type FounderTicketType = 'approval_request' | 'question' | 'status_update' | 'tool_request' | 'project_creation' | 'content_preview';
export type FounderTicketStatus = 'open' | 'waiting' | 'approved' | 'done';
export type FounderTicketPriority = 'low' | 'normal' | 'high' | 'urgent';

export interface FounderTicket {
  id: number;
  company_id: number;
  agent_id: number;
  ticket_type: FounderTicketType;
  title: string;
  description: string;
  status: FounderTicketStatus;
  priority: FounderTicketPriority;
  founder_comment: string | null;
  founder_comment_ack: number;
  reminder_sent_at: string | null;
  user_id: number;
  created_at: string;
  updated_at: string;
}

export async function listTickets(status?: FounderTicketStatus): Promise<FounderTicket[]> {
  const url = status ? `/api/founder-board/tickets?status=${status}` : '/api/founder-board/tickets';
  const res = await fetch(url);
  if (!res.ok) throw new Error('Fehler beim Laden der Tickets');
  return res.json() as Promise<FounderTicket[]>;
}

export interface MentionData {
  agent_id: number;
  agent_name: string;
}

export async function respondToTicket(
  ticketId: number,
  comment: string,
  newStatus: FounderTicketStatus,
  mentions?: MentionData[],
): Promise<FounderTicket> {
  const body: Record<string, unknown> = { comment, new_status: newStatus };
  if (mentions && mentions.length > 0) {
    body.mentions = mentions;
  }
  const res = await fetch(`/api/founder-board/tickets/${ticketId}/respond`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json() as { error: string };
    throw new Error(err.error ?? 'Fehler beim Antworten auf das Ticket');
  }
  return res.json() as Promise<FounderTicket>;
}

export async function approveToolRequest(ticketId: number): Promise<FounderTicket> {
  const res = await fetch(`/api/founder-board/tickets/${ticketId}/approve-tool`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const err = await res.json() as { error: string };
    throw new Error(err.error ?? 'Fehler beim Genehmigen der Tool-Anfrage');
  }
  return res.json() as Promise<FounderTicket>;
}

export async function deleteTicket(ticketId: number): Promise<void> {
  const res = await fetch(`/api/founder-board/tickets/${ticketId}`, {
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 204) {
    const err = await res.json() as { error: string };
    throw new Error(err.error ?? 'Fehler beim Loeschen des Tickets');
  }
}

export function parseToolRequestMeta(description: string): { tool_name: string; reason: string } | null {
  try {
    const parsed = JSON.parse(description) as { tool_name?: string; reason?: string };
    if (parsed.tool_name && parsed.reason) {
      return { tool_name: parsed.tool_name, reason: parsed.reason };
    }
    return null;
  } catch {
    return null;
  }
}

export function ticketTypeLabel(type: FounderTicketType): string {
  const labels: Record<FounderTicketType, string> = {
    approval_request: 'Genehmigung erforderlich',
    question: 'Rueckfrage',
    status_update: 'Status Update',
    tool_request: 'Tool-Anfrage',
    project_creation: 'Projekt-Anfrage',
    content_preview: 'Instagram Vorschau',
  };
  return labels[type] ?? type;
}

export function parseProjectCreationMeta(description: string): { project_name: string; reason: string } | null {
  try {
    const parsed = JSON.parse(description) as { project_name?: string; reason?: string };
    if (parsed.project_name && parsed.reason) {
      return { project_name: parsed.project_name, reason: parsed.reason };
    }
    return null;
  } catch {
    return null;
  }
}

export async function approveProjectCreation(
  ticketId: number,
  projectPath: string,
  gitInit?: boolean,
  gitRemoteUrl?: string,
): Promise<FounderTicket> {
  const res = await fetch(`/api/founder-board/tickets/${ticketId}/approve-project`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ path: projectPath, git_init: gitInit, git_remote_url: gitRemoteUrl }),
  });
  if (!res.ok) {
    const err = await res.json() as { error: string };
    throw new Error(err.error ?? 'Fehler beim Anlegen des Projekts');
  }
  return res.json() as Promise<FounderTicket>;
}

export interface BrowseEntry {
  name: string;
  type: 'directory';
}

export interface BrowseResult {
  current: string;
  parent: string | null;
  entries: BrowseEntry[];
}

export async function browseFilesystem(browsePath?: string): Promise<BrowseResult> {
  const params = browsePath ? `?path=${encodeURIComponent(browsePath)}` : '';
  const res = await fetch(`/api/projects/browse${params}`);
  if (!res.ok) {
    const err = await res.json() as { error: string };
    throw new Error(err.error ?? 'Fehler beim Laden des Verzeichnisses');
  }
  return res.json() as Promise<BrowseResult>;
}

export interface ContentPreviewMeta {
  post_type: 'post' | 'carousel' | 'reel';
  caption: string;
  hashtags: string[];
  image_urls: string[];
  drive_file_ids?: string[];
  posting_time?: string;
  reel_video_url?: string;
}

export function parseContentPreviewMeta(description: string): ContentPreviewMeta | null {
  try {
    // Description may have appended text after JSON (founder comments, agent replies).
    // Extract only the JSON object by finding the matching closing brace.
    const jsonStart = description.indexOf('{');
    if (jsonStart === -1) return null;
    let depth = 0;
    let jsonEnd = -1;
    for (let i = jsonStart; i < description.length; i++) {
      if (description[i] === '{') depth++;
      else if (description[i] === '}') {
        depth--;
        if (depth === 0) { jsonEnd = i; break; }
      }
    }
    if (jsonEnd === -1) return null;
    const parsed = JSON.parse(description.slice(jsonStart, jsonEnd + 1)) as ContentPreviewMeta;
    if (parsed.caption && parsed.image_urls) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Convert Google Drive URLs to embeddable thumbnail URLs.
 * Handles: drive.google.com/file/d/ID/view, drive.google.com/open?id=ID,
 * drive.google.com/thumbnail?id=ID, and passes through already-valid direct URLs.
 */
export function driveUrlToThumbnail(url: string): string {
  // Already a direct googleusercontent URL
  if (url.includes('lh3.googleusercontent.com')) return url;
  // drive.google.com/thumbnail?id=FILE_ID(&sz=...) — needs conversion too
  const thumbMatch = url.match(/drive\.google\.com\/thumbnail\?.*id=([^&]+)/);
  if (thumbMatch) return `https://lh3.googleusercontent.com/d/${thumbMatch[1]}`;
  // drive.google.com/file/d/FILE_ID/...
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([^/?]+)/);
  if (fileMatch) return `https://lh3.googleusercontent.com/d/${fileMatch[1]}`;
  // drive.google.com/open?id=FILE_ID
  const openMatch = url.match(/drive\.google\.com\/open\?id=([^&]+)/);
  if (openMatch) return `https://lh3.googleusercontent.com/d/${openMatch[1]}`;
  // drive.google.com/uc?id=FILE_ID
  const ucMatch = url.match(/drive\.google\.com\/uc\?.*id=([^&]+)/);
  if (ucMatch) return `https://lh3.googleusercontent.com/d/${ucMatch[1]}`;
  return url;
}

export async function approveContentPreview(ticketId: number, editedCaption?: string): Promise<FounderTicket> {
  const body: Record<string, unknown> = {};
  if (editedCaption) body.edited_caption = editedCaption;
  const res = await fetch(`/api/founder-board/tickets/${ticketId}/approve-content`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json() as { error: string };
    throw new Error(err.error ?? 'Fehler beim Genehmigen des Contents');
  }
  return res.json() as Promise<FounderTicket>;
}

export async function listTicketsByCompanyAndType(companyId: number, ticketType: string): Promise<FounderTicket[]> {
  const res = await fetch(`/api/founder-board/tickets?companyId=${companyId}`);
  if (!res.ok) throw new Error('Fehler beim Laden der Tickets');
  const tickets = await res.json() as FounderTicket[];
  return tickets.filter(t => t.ticket_type === ticketType);
}

export function statusLabel(status: FounderTicketStatus): string {
  const labels: Record<FounderTicketStatus, string> = {
    open: 'Offen',
    waiting: 'Wartet auf mich',
    approved: 'Genehmigt',
    done: 'Erledigt',
  };
  return labels[status] ?? status;
}
